import type { LeagueIconView } from "../../server/matchups.server";

// The League catalogs (icons, tooltips, champion art, rune trees) stay on the server. A Bible
// chapter arrives with this dictionary of exactly what its sections name, built by
// server/bible-league.server.ts; the components below look things up in it and fall back to the
// author's words for anything it doesn't know.

export type RuneBoard = { label: string; tree: string; rows: string[][]; selected: string[][] };
export type RuneBoards = { boards: RuneBoard[]; unknown: string[] };
export type ChampionView = {
  displayName: string | null;
  portrait: string | null;
  portraitSrcSet: string | null;
};

export type BibleLeague = {
  /** `${kind}:${name}`: a rune, item, summoner spell or ability icon with its tooltip. */
  icons: Record<string, LeagueIconView>;
  /** A champion by the id or name an author wrote. */
  champions: Record<string, ChampionView>;
  /** `${champion}:${slot}`: an ability's art. */
  abilities: Record<string, string | null>;
  /** An item reference (itemKey) as the item catalog names it. */
  items: Record<string, string | null>;
  /** A set of rune names (boardsKey), laid out on their trees. */
  boards: Record<string, RuneBoards>;
};

export const emptyLeague: BibleLeague = {
  icons: {},
  champions: {},
  abilities: {},
  items: {},
  boards: {},
};

export type IconKind = LeagueIconView["kind"];

export const iconKey = (kind: IconKind, name: string) => `${kind}:${name}`;
export const itemKey = (id: string, label: string) => JSON.stringify([id, label]);
export const abilityKey = (champion: string, slot: string) => `${champion}:${slot}`;
export const boardsKey = (names: string[], shards?: string[]) =>
  JSON.stringify([names, shards ?? null]);

/** A League icon's view, or the author's name without an icon. */
export function leagueIconView(
  league: BibleLeague,
  kind: IconKind,
  name: string,
  options: { selected?: boolean; keystone?: boolean } = {},
): LeagueIconView {
  const view = league.icons[iconKey(kind, name)] ?? {
    kind,
    name,
    icon: null,
    label: name,
    tip: null,
    note: "",
  };
  return { ...view, ...options };
}

/** leagueIcon(kind, name): the icon's address, or null. */
export const iconSource = (league: BibleLeague, kind: IconKind, name: string) =>
  league.icons[iconKey(kind, name)]?.icon ?? null;

/** leagueItemReference(id, label)?.name ?? label. */
export const itemName = (league: BibleLeague, id: string, label: string) =>
  league.items[itemKey(id, label)] ?? label;

/** championId && slot !== "AA" ? leagueIcon("ability", `${championId}:${slot}`) : null. */
export const abilityArt = (league: BibleLeague, champion: string | undefined, slot: string) =>
  champion && slot !== "AA" ? (league.abilities[abilityKey(champion, slot)] ?? null) : null;

export const champion = (league: BibleLeague, id: string): ChampionView =>
  league.champions[id] ?? { displayName: null, portrait: null, portraitSrcSet: null };

/** runeBoards(names, shards), from the dictionary. */
export function runeBoards(league: BibleLeague, names: string[], shards?: string[]): RuneBoards {
  const exact = league.boards[boardsKey(names, shards)];
  if (exact) return exact;
  // While an editor row changes, its already loaded paths can draw the new choice immediately.
  // A different path still waits for its own bounded dictionary from the server.
  const previous = Object.values(league.boards).find(
    ({ boards }) =>
      boards.some(
        (board) => board.label === "Primary" && board.rows[0]?.some((name) => names.includes(name)),
      ) &&
      names.every((name) => boards.some((board) => board.rows.some((row) => row.includes(name)))),
  );
  if (!previous) return { boards: [], unknown: names };
  return {
    boards: previous.boards.map((board) => ({
      ...board,
      selected:
        board.label === "Shards" && !shards
          ? board.selected
          : board.rows.map((row, index) =>
              board.label === "Shards" && shards
                ? row.filter((name) => name === shards[index])
                : row.filter((name) => names.includes(name)),
            ),
    })),
    unknown: [],
  };
}
